import { z } from "zod";
import { countryCode, isoDate, optionalEmail, optionalText, phoneInput, text, uuid } from "./common";

export const GENDERS = ["female", "male", "other", "undisclosed"] as const;
export const FAMILY_RELATIONSHIPS = ["self", "spouse", "child", "parent", "sibling", "grandparent", "grandchild", "guardian", "other"] as const;

export const patientContactInput = z.object({
  name: text(200),
  relationship: optionalText(50),
  phone: phoneInput,
  email: optionalEmail,
  isEmergency: z.boolean().default(true),
  notes: optionalText(500),
});

export const patientInput = z.object({
  fullName: text(200),
  preferredName: optionalText(100),
  gender: z.enum(GENDERS).nullable().default(null),
  dateOfBirth: isoDate.nullable().default(null),
  dobIsEstimated: z.boolean().default(false),
  phone: phoneInput,
  whatsappPhone: phoneInput,
  /** When true, WhatsApp = phone (the common case at a front desk). */
  whatsappSameAsPhone: z.boolean().default(true),
  email: optionalEmail,
  addressLine1: optionalText(200),
  addressLine2: optionalText(200),
  city: optionalText(100),
  region: optionalText(100),
  postalCode: optionalText(20),
  countryCode: countryCode.nullable().default(null),
  occupation: optionalText(100),
  preferredLanguage: optionalText(20),
  sourceId: uuid.nullable().default(null),
  referredByPatientId: uuid.nullable().default(null),
  referralNote: optionalText(500),
  primaryBranchId: uuid.nullable().default(null),
  notes: optionalText(4000),
  tagIds: z.array(uuid).max(50).default([]),
  contacts: z.array(patientContactInput).max(10).default([]),
});
export type PatientInput = z.infer<typeof patientInput>;

export const createPatientInput = patientInput.extend({
  /** Set after the receptionist has seen the duplicate warning and decided this is a new person. */
  confirmNotDuplicate: z.boolean().default(false),
});
export const updatePatientInput = patientInput.extend({
  version: z.number().int().min(1),
  status: z.enum(["active", "inactive", "deceased"]).default("active"),
});

export const patientSearchQuery = z.object({
  q: z.string().trim().max(100).optional(),
  tagId: uuid.optional(),
  sourceId: uuid.optional(),
  status: z.enum(["active", "inactive", "deceased"]).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().max(200).optional(),
});

export const duplicateCheckInput = z.object({
  fullName: text(200),
  phone: phoneInput,
  email: optionalEmail,
  dateOfBirth: isoDate.nullable().default(null),
});

export interface PatientListItem {
  id: string;
  patientCode: string;
  fullName: string;
  gender: string | null;
  dateOfBirth: string | null;
  phone: string | null;
  email: string | null;
  status: string;
  tags: { id: string; name: string; color: string | null }[];
  createdAt: string;
}

export interface Patient extends PatientListItem {
  preferredName: string | null;
  dobIsEstimated: boolean;
  whatsappPhone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  countryCode: string | null;
  occupation: string | null;
  preferredLanguage: string | null;
  source: { id: string; name: string } | null;
  referredBy: { id: string; fullName: string; patientCode: string } | null;
  referralNote: string | null;
  primaryBranchId: string | null;
  notes: string | null;
  contacts: (z.infer<typeof patientContactInput> & { id: string })[];
  family: null | {
    id: string;
    name: string;
    members: { patientId: string; fullName: string; patientCode: string; relationship: string; isPrimary: boolean }[];
  };
  version: number;
  updatedAt: string;
}

export interface DuplicateCandidate {
  id: string;
  patientCode: string;
  fullName: string;
  phone: string | null;
  dateOfBirth: string | null;
  /** Why it matched, strongest first. */
  reasons: ("phone" | "email" | "name_dob" | "similar_name_phone")[];
  score: number;
}

export const tagInput = z.object({ name: text(50), color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().default(null) });
export const sourceInput = z.object({
  code: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/),
  name: text(100),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
  isActive: z.boolean().default(true),
});

export const familyInput = z.object({
  name: text(200),
  notes: optionalText(1000),
  members: z
    .array(z.object({ patientId: uuid, relationship: z.enum(FAMILY_RELATIONSHIPS), isPrimary: z.boolean().default(false) }))
    .min(1)
    .max(30)
    .refine((m) => m.filter((x) => x.isPrimary).length <= 1, "Only one primary contact")
    .refine((m) => new Set(m.map((x) => x.patientId)).size === m.length, "A patient is listed twice"),
});
